import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, Mail, MapPin, Clock, CheckCircle2 } from "lucide-react";
import { SOCIALS } from "@/data/site";
import StudioBackdrop from "@/components/podcast/StudioBackdrop";

const TYPES = [
  { value: "general", label: "General" },
  { value: "booking", label: "Booking / Hosting" },
  { value: "press", label: "Press / Media" },
  { value: "sponsorship", label: "Sponsorship" },
  { value: "other", label: "Other" },
];

const INITIAL = { name: "", email: "", subject: "", message: "", inquiry_type: "general" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact-inquiries", form);
      toast.success("Message sent. We'll get back to you.");
      setSubmitted(true);
      setForm(INITIAL);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Submission failed";
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-20 min-h-screen bg-[#0A0A0A] relative">
      <StudioBackdrop opacity={0.1} size={240} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E10600] mb-3">
              Get In Touch
            </p>
            <h1 className="font-display text-6xl md:text-8xl uppercase tracking-tight leading-[0.9]">
              Contact &<br /><span className="text-[#E10600]">Booking</span>
            </h1>
            <p className="mt-6 text-zinc-400">
              Booking inquiries, press requests, brand partnerships, or just to say what's
              up — drop us a line and we'll route it to the right place.
            </p>

            <div className="mt-10 space-y-5 border-t border-[#262626] pt-8">
              <Info icon={Mail} label="Email" value={SOCIALS.email} href={`mailto:${SOCIALS.email}`} />
              <Info icon={Clock} label="Live Stream" value="Mondays · 7PM – 9PM EST" />
              <Info icon={MapPin} label="Based / Filmed" value="New York City · Boston" />
            </div>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="border border-[#E10600] p-10 bg-[#121212] text-center"
              >
                <CheckCircle2 className="w-12 h-12 text-[#E10600] mx-auto mb-4" />
                <h2 className="font-display text-4xl uppercase">Message Sent</h2>
                <p className="mt-4 text-zinc-400">
                  We typically respond within 48 hours. Booking and press inquiries get
                  priority routing.
                </p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setSubmitted(false)}
                  className="btn-secondary mt-8"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form
                data-testid="contact-form"
                onSubmit={submit}
                className="border border-[#262626] bg-[#0F0F0F] p-6 md:p-10 space-y-6"
              >
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 font-mono block mb-3">
                    Inquiry Type
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {TYPES.map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        data-testid={`contact-type-${t.value}`}
                        onClick={() => setForm({ ...form, inquiry_type: t.value })}
                        className={`px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold border transition-colors ${
                          form.inquiry_type === t.value
                            ? "border-[#E10600] bg-[#E10600] text-white"
                            : "border-[#262626] text-zinc-400 hover:text-white"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <label className="block">
                    <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 font-mono block mb-1">
                      Name *
                    </span>
                    <input data-testid="contact-input-name" required value={form.name} onChange={update("name")} className="input-edge" placeholder="Your name" />
                  </label>
                  <label className="block">
                    <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 font-mono block mb-1">
                      Email *
                    </span>
                    <input data-testid="contact-input-email" required type="email" value={form.email} onChange={update("email")} className="input-edge" placeholder="you@example.com" />
                  </label>
                </div>

                <label className="block">
                  <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 font-mono block mb-1">
                    Subject *
                  </span>
                  <input data-testid="contact-input-subject" required value={form.subject} onChange={update("subject")} className="input-edge" placeholder="What's this about?" />
                </label>

                <label className="block">
                  <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 font-mono block mb-1">
                    Message *
                  </span>
                  <textarea data-testid="contact-input-message" required rows={6} value={form.message} onChange={update("message")} className="input-edge resize-none" placeholder="Tell us what you need." />
                </label>

                <div className="pt-4 flex flex-wrap items-center gap-4 justify-between">
                  <p className="text-xs text-zinc-500 font-mono">Average response: 48 hours</p>
                  <button
                    data-testid="contact-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="btn-primary disabled:opacity-50"
                  >
                    {loading ? "Sending…" : "Send Message"}
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-start gap-4 group">
      <span className="w-11 h-11 border border-[#262626] group-hover:border-[#E10600] flex items-center justify-center transition-colors flex-shrink-0">
        <Icon className="w-5 h-5 text-[#E10600]" />
      </span>
      <div>
        <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-mono">{label}</p>
        <p className="mt-1 text-white group-hover:text-[#E10600] transition-colors">{value}</p>
      </div>
    </div>
  );
  return href ? <a href={href}>{inner}</a> : inner;
}
